import { useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { X, LogOut, User, Shield, Store } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { logOut } from "@/store/actions/authActions";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useProfile } from "@/hooks";

interface MoreItem {
  id: string;
  title: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
  section?: string;
}

interface MoreMenuProps {
  isOpen: boolean;
  onClose: () => void;
  items: MoreItem[];
  onItemClick: (path: string) => void;
  userType: "user" | "admin" | "vendor" | "index";
}

export function MoreMenu({
  isOpen,
  onClose,
  items,
  onItemClick,
  userType,
}: MoreMenuProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const { data: profile, isLoading, isError } = useProfile(userId);

  // Group items by section
  const groupedItems = items.reduce(
    (acc, item) => {
      const section = item.section || "Other";
      if (!acc[section]) acc[section] = [];
      acc[section].push(item);
      return acc;
    },
    {} as Record<string, MoreItem[]>,
  );

  const sectionOrder = [
    "Dashboard",
    "Trading",
    "Business",
    "Management",
    "Account",
    "Support",
    "System",
    "Other",
  ];
  const sortedSections = Object.keys(groupedItems).sort(
    (a, b) => sectionOrder.indexOf(a) - sectionOrder.indexOf(b),
  );

  // Handle escape key
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);

  const handleLogout = () => {
    dispatch(logOut());
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/login");
    onClose();
  };

  const getHeaderColor = () => {
    switch (userType) {
      case "admin":
        return "from-violet-500 to-purple-600";
      case "vendor":
        return "from-emerald-500 to-teal-600";
      default:
        return "from-blue-500 to-cyan-600";
    }
  };

  const getIcon = () => {
    switch (userType) {
      case "admin":
        return Shield;
      case "vendor":
        return Store;
      default:
        return User;
    }
  };

  const HeaderIcon = getIcon();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl shadow-2xl",
          "animate-in slide-in-from-bottom duration-300 ease-out",
          "max-h-[85vh] overflow-hidden flex flex-col",
        )}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2" onClick={onClose}>
          <div className="w-12 h-1.5 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div
          className={cn("px-5 pb-5 pt-2 bg-gradient-to-r", getHeaderColor())}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <HeaderIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {userType === "admin"
                    ? "Admin Menu"
                    : userType === "vendor"
                      ? "Vendor Menu"
                      : "Menu"}
                </h3>
                <p className="text-white/70 text-sm">
                  {profile?.email || "User"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-3 place-items-stretch gap-4">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => onItemClick(item.path)}
                  className="w-full flex flex-col items-center justify-start gap-2 active:scale-95 transition-transform"
                >
                  <div className="relative w-16 h-16 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center">
                    <Icon className="h-7 w-7" />

                    {item.badge && (
                      <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </div>

                  <span className="text-xs text-center leading-tight line-clamp-2">
                    {item.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mx-2">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 p-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 active:scale-95 transition-all duration-200"
          >
            <div className="h-10 w-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <LogOut className="h-5 w-5 text-red-500" />
            </div>
            <span className="text-sm font-medium text-red-500">Logout</span>
          </button>
          <button
            onClick={() => {
              onClose();
              navigate(
                userType === "vendor"
                  ? "/vendor/settings"
                  : "/dashboard/profile",
              );
            }}
            className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted active:scale-95 transition-all duration-200"
          >
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <span className="text-sm font-medium">Profile</span>
          </button>
        </div>
      </div>

      {/* Safe area padding */}
      <div className="h-safe-area-inset-bottom bg-background" />
    </div>
  );
}
